/**
 * AppointmentPreview — BLOCK level (Claude Design round 7). The centre pane of the new-design Today
 * screen: a person-first header card for the SELECTED appointment, then its pre-visit summary as the
 * chart's section cards. Selecting never opens the visit; the actions below do.
 *
 * Actions follow the appointment's real state (no invented action):
 *   Booked                → Mark arrived · Open encounter (disabled: needs Arrived)
 *   Arrived / Checked in  → Open encounter
 *   another open status   → Open encounter disabled, with the app's own block reason
 *   In progress (draft)   → Open encounter
 *   Completed             → Open encounter
 * They all go to the encounter exactly as a card click did before.
 */
import { Stethoscope } from 'lucide-react';
import { Avatar, Button, StatusChip, ClinicalTermRow, cn, type IpsSection } from '@scripsteam/scrips-signal-ds';
import { V2_CARD_SURFACE, V2_EYEBROW, V2_META, V2_BODY, V2_PANEL_TITLE } from '@/shared/ui/design-v2';
import { isArrivedStatus, appointmentStartBlockReason } from '@/encounter/lib/is-arrived';
import { TodayStatusChip } from '../components/today-status-chip';
import { glyphKindForAppointment, visitEnded } from '../lib/appointment-glyph';
import type { TodayItem } from './today-types';
import { PreVisitSectionCard, type Wayfind } from './PreVisitSectionCard';

const MODALITY = { office: 'Office', home: 'Home visit', virtual: 'Virtual' } as const;
const KIND = { 'first-time': 'First visit', 'follow-up': 'Follow-up' } as const;

/** The pre-visit sections the preview shows, in the chart's order, with their wayfinding ink. */
const SECTIONS: { id: string; title: string; wayfind: Wayfind; fhir: string; empty: string }[] = [
  { id: 'flags', title: 'Patient flags', wayfind: 'neutral', fhir: 'Flag', empty: 'No flags recorded' },
  { id: 'allergies', title: 'Allergies', wayfind: 'allergies', fhir: 'AllergyIntolerance', empty: 'No allergies recorded' },
  { id: 'active-problems', title: 'Active problems', wayfind: 'problems', fhir: 'Condition', empty: 'No active problems recorded' },
  { id: 'medications', title: 'Medications', wayfind: 'medications', fhir: 'MedicationStatement', empty: 'No medications recorded' },
  { id: 'vaccinations', title: 'Vaccinations', wayfind: 'neutral', fhir: 'Immunization', empty: 'No vaccinations recorded' },
];

export interface AppointmentPreviewProps {
  item: TodayItem;
  /** The clock a running visit is judged by ("Note to sign" only after the visit's end). Default: now. */
  nowMs?: number;
  /** The patient's GetPatientSummary, mapped by patientSummaryToIpsSections. Null while loading or unavailable. */
  sections: IpsSection[] | null;
  sectionsLoading: boolean;
  sectionsError?: boolean;
  onMarkArrived: (id: string) => void;
  markingArrived?: boolean;
  /** True while the server's status list (the PUT body source) is not available. */
  markArrivedUnavailable?: boolean;
  onOpenVisit: (id: string) => void;
}

export function AppointmentPreview({
  item,
  sections,
  sectionsLoading,
  sectionsError,
  onMarkArrived,
  markingArrived,
  markArrivedUnavailable,
  onOpenVisit,
  nowMs = Date.now(),
}: AppointmentPreviewProps) {
  const c = item.card;
  const booked = item.statusCode === 'Booked';
  const arrived = isArrivedStatus(item.statusCode);
  const blockReason = item.bucket === 'upcoming' && !booked ? appointmentStartBlockReason(item.statusCode) : undefined;
  const identity = [[c.age, c.gender].filter(Boolean).join(' · '), c.mrn ? `MRN ${c.mrn}` : null]
    .filter(Boolean)
    .join(' · ');
  const visit = [
    c.time,
    c.room,
    c.modality ? MODALITY[c.modality] : null,
    c.appointmentKind ? KIND[c.appointmentKind] : null,
    c.isRecurring ? 'Recurring' : null,
  ]
    .filter(Boolean)
    .join(' · ');
  const byId = new Map((sections ?? []).map((s) => [s.id, s]));

  return (
    <>
      <section
        aria-label={c.patientName}
        data-slot="today-preview-header"
        className={cn('flex flex-col gap-4 rounded-card p-6', V2_CARD_SURFACE)}
      >
        <div className="flex min-w-0 items-start gap-4">
          <Avatar name={c.patientName} src={c.photoUrl} size="lg" tone="patient" />
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-2">
              <h2 className={cn('m-0 min-w-0 flex-1 text-text-primary', V2_PANEL_TITLE)}>{c.patientName}</h2>
              <TodayStatusChip kind={glyphKindForAppointment(item.statusCode, item.bucket, visitEnded(item, nowMs))}>{c.status}</TodayStatusChip>
            </div>
            {identity && <span className={V2_META}>{identity}</span>}
          </div>
        </div>
        <dl className="m-0 flex flex-col gap-2">
          {visit && (
            <div className="flex gap-4">
              <dt className={cn('w-16 shrink-0', V2_EYEBROW)}>Visit</dt>
              <dd className={cn('m-0 min-w-0 break-words tabular-nums', V2_BODY)}>{visit}</dd>
            </div>
          )}
          <div className="flex gap-4">
            <dt className={cn('w-16 shrink-0', V2_EYEBROW)}>Reason</dt>
            <dd className={cn('m-0 min-w-0 break-words', V2_BODY)}>{c.reasonForVisit || '—'}</dd>
          </div>
        </dl>
        {c.vaccinationPending && (
          <div className="flex">
            <StatusChip semantic="warning" tone="soft">Vaccination pending</StatusChip>
          </div>
        )}
        <div className="flex flex-wrap items-center gap-3 border-0 border-t border-solid border-border-default pt-4">
          {item.bucket === 'upcoming' && booked && (
            <Button variant="secondary" disabled={markingArrived || markArrivedUnavailable} onClick={() => onMarkArrived(item.id)}>
              {markingArrived ? 'Marking arrived…' : 'Mark arrived'}
            </Button>
          )}
          {item.bucket === 'upcoming' && (
            <Button variant="primary" icon={<Stethoscope className="size-4" aria-hidden />} disabled={!arrived} onClick={() => onOpenVisit(item.id)}>
              Open encounter
            </Button>
          )}
          {item.bucket === 'in-progress' && (
            <Button variant="primary" icon={<Stethoscope className="size-4" aria-hidden />} onClick={() => onOpenVisit(item.id)}>
              Open encounter
            </Button>
          )}
          {item.bucket === 'completed' && (
            <Button variant="secondary" icon={<Stethoscope className="size-4" aria-hidden />} onClick={() => onOpenVisit(item.id)}>
              Open encounter
            </Button>
          )}
          {item.bucket === 'upcoming' && booked && (
            <span className={V2_META}>Open encounter needs the patient marked arrived.</span>
          )}
          {blockReason && <span className={V2_META}>{blockReason}</span>}
        </div>
      </section>

      <h3 className={cn('m-0 px-1 pt-2', V2_EYEBROW)}>Pre-visit summary</h3>
      {sectionsError ? (
        <p className={cn('m-0 px-1 text-text-secondary', V2_BODY)}>Couldn't load the pre-visit summary.</p>
      ) : (
        <div className="@container">
          {/* Columns follow the centre's LIVE width (a container query, sheet §1c) and a card is never
              narrower than 320 (the round-7 rule, desk D-52): 1 · 2 from 672 (@2xl) · 3 from 1024
              (@5xl). No 4th column: it would need a container token, and this grid is interim (D-50
              moves these sections into the chart curtain). Names wrap; nothing truncates. */}
          <div className="grid grid-cols-1 items-start gap-2 @2xl:grid-cols-2 @5xl:grid-cols-3">
            {SECTIONS.map((s) => {
              const sec = byId.get(s.id);
              const entries = sec?.entries ?? [];
              return (
                <PreVisitSectionCard
                  key={s.id}
                  fhir={s.fhir}
                  title={s.title}
                  wayfind={s.wayfind}
                  count={sec ? entries.length : undefined}
                  emptyText={s.empty}
                  loading={sectionsLoading}
                >
                  {entries.map((e) => (
                    <ClinicalTermRow
                      key={e.id}
                      term={e.primary}
                      secondary={e.secondary}
                      kind={s.id === 'allergies' ? 'allergy' : 'dx'}
                      state="active"
                      showDot={s.id === 'active-problems'}
                      readOnly
                    />
                  ))}
                </PreVisitSectionCard>
              );
            })}
          </div>
        </div>
      )}
    </>
  );
}
